<template>
  <div>
    <div class="editor">
      <editor-content
        :editor="editor"
        class="template-editor" />
    </div>
    <div class="suggestion-list" v-show="showSuggestions" ref="suggestions">
      <template v-if="hasResults">
        <div
          v-for="(item, i) in filteredVars"
          :key="`var-${i}`"
          class="suggestion-list__item"
          :class="{ 'is-selected': navigatedVarIndex === i }"
          @click="selectVar(item)">
          {{ item }}
        </div>
      </template>
      <div v-else>
        No hay variables
      </div>
    </div>
  </div>
</template>

<script>
import { Editor, EditorContent } from 'tiptap';
import { Mention, Placeholder } from 'tiptap-extensions';
import tippy from 'tippy.js';
import filtersMixin from '@/mixins/workflowFilters';

export default {
  props: ['value', 'appendVars'],
  components: {
    EditorContent,
  },
  beforeDestroy() {
    this.editor.destroy();
  },
  mixins: [
    filtersMixin,
  ],
  data() {
    return {
      text: '',
      editor: new Editor({
        onUpdate: ({ getHTML }) => {
          this.text = this.removeHtmlTags(getHTML());
        },
        extensions: [
          new Placeholder({
            emptyClass: 'is-empty',
            emptyNodeText: 'Escriba aqui un texto...',
            showOnlyWhenEditable: true,
          }),
          new Mention({
            // a list of all suggested items
            items: () => [
              '@nombre',
              '@apellido',
              '@nombre_completo',
              '@folio',
              '@fecha_envio',
              '@monto',
              '@moneda',
              '@referencia',
              '@fecha_vencimiento',
              '@mañana',
              ...this.templateVars.map(item => `@${item}`),
              ...(this.appendVars || []).map(item => `@${item}`),
            ],
            // is called when a suggestion starts
            onEnter: ({
              items, query, range, command, virtualNode,
            }) => {
              this.query = query;
              this.filteredVars = items;
              this.suggestionRange = range;
              this.renderPopup(virtualNode);
              // we save the command for inserting a selected mention
              // this allows us to call it inside of our custom popup
              // via keyboard navigation and on click
              this.insertMention = command;
            },
            // is called when a suggestion has changed
            onChange: ({
              items, query, range, virtualNode,
            }) => {
              this.query = query;
              this.filteredVars = items;
              this.suggestionRange = range;
              this.navigatedVarIndex = 0;
              this.renderPopup(virtualNode);
            },
            // is called when a suggestion is cancelled
            onExit: () => {
              // reset all saved values
              this.query = null;
              this.filteredVars = [];
              this.suggestionRange = null;
              this.navigatedVarIndex = 0;
              if (this.popup) {
                this.popup.destroy();
                this.popup = null;
              }
              if (this.observer) {
                this.observer.disconnect();
              }
            },
            // is called on every keyDown event while a suggestion is active
            onKeyDown: ({ event }) => {
              let result = false;
              if (event.keyCode === 38) {
                this.navigatedVarIndex = (
                  (
                    this.navigatedVarIndex + this.filteredVars.length
                  ) - 1
                ) % this.filteredVars.length;
                result = true;
              } else if (event.keyCode === 40) {
                this.navigatedVarIndex = (this.navigatedVarIndex + 1) % this.filteredVars.length;
                result = true;
              } else if (event.keyCode === 13) {
                const item = this.filteredVars[this.navigatedVarIndex];
                if (item) {
                  this.selectVar(item);
                }
                result = true;
              }
              return result;
            },
            onFilter: (items, query) => {
              if (!query) {
                return items;
              }
              return items.filter(item => item.startsWith(`@${query}`));
            },
          }),
        ],
      }),
      query: null,
      suggestionRange: null,
      observer: null,
      filteredVars: [],
      navigatedVarIndex: 0,
      insertMention: () => {},
    };
  },
  computed: {
    hasResults() {
      return this.filteredVars.length;
    },
    showSuggestions() {
      return this.query || this.hasResults;
    },
  },
  methods: {
    removeHtmlTags(text) {
      return text.replace(/(<([^>]+)>)/ig, '').trim();
    },
    renderPopup(node) {
      if (!this.popup) {
        this.popup = tippy(node, {
          content: this.$refs.suggestions,
          trigger: 'mouseenter',
          interactive: true,
          theme: 'light',
          placement: 'top-start',
          inertia: true,
          duration: [400, 200],
          showOnInit: true,
          arrow: true,
          arrowType: 'round',
        });
        if (MutationObserver) {
          this.observer = new MutationObserver(() => {
            this.popup.popperInstance.scheduleUpdate();
          });
          this.observer.observe(this.$refs.suggestions, {
            childList: true,
            subtree: true,
            characterData: true,
          });
        }
      }
    },
    selectVar(item) {
      const text = item.replace(/@/, '');
      this.insertMention({
        range: this.suggestionRange,
        attrs: {
          id: text,
          label: text,
        },
      });
      this.editor.focus();
    },
  },
  watch: {
    value() {
      if (this.value !== this.text && this.value) {
        const splitted = this.value.split(' ');
        let html = '';
        if (splitted.length > 1) {
          for (let i = 0; i < splitted.length; i += 1) {
            const current = splitted[i];
            if (current.startsWith('@')) {
              const variable = current.replace(/@/, '');
              const mention = `<span class="mention" data-mention-id="${variable}">${current}</span>`;
              this.editor.setContent(`${html} ${mention}`);
            } else {
              this.editor.setContent(html + current);
            }
            this.editor.setContent(`${this.editor.getHTML()} `);
            html = this.editor.getHTML().replace(/<\/?p[^>]*>/g, '');
            if (i !== splitted.length - 1) {
              html += ' ';
            }
          }
        } else if (this.value.startsWith('@')) {
          this.editor.setContent('@');
          this.suggestionRange = {
            from: 1,
            to: 2,
          };
          this.selectVar(this.value);
        } else {
          this.editor.setContent(this.value);
        }
        this.text = this.removeHtmlTags(this.editor.getHTML());
      } else if (!this.value) {
        this.editor.setContent('');
        this.text = '';
      }
    },
    text() {
      this.$emit('input', this.text);
    },
  },
};
</script>

<style scoped>
.template-editor >>> .ProseMirror {
	display: block !important;
	width: 100% !important;
	border: 0 !important;
	padding: 10px 5px !important;
  background: #fff no-repeat !important;
	background-image: linear-gradient(to bottom, #000, #000), linear-gradient(to bottom, rgba(0,0,0,.42), rgba(0,0,0,.42)) !important;
	background-size: 0 2px, 100% 1px !important;
	background-position: 50% 100%, 50% 100% !important;
  transition: background-size 0.3s cubic-bezier(0.64, 0.09, 0.08, 1) !important;
}
.template-editor >>> .ProseMirror:focus {
	background-size: 100% 2px, 100% 1px !important;
	outline: none;
}
.template-editor >>> .ProseMirror {
  height: 180px !important;
  overflow: auto !important;
}
.template-editor >>> p.is-empty:first-child::before {
  content: attr(data-empty-text);
  float: left;
  color: #aaa;
  pointer-events: none;
  height: 0;
  font-style: italic;
}
.template-editor >>> .mention, .template-editor >>> .mention-suggestion {
  color: rgba(0,0,0,.6);
}
.template-editor >>> .mention {
  background: rgba(0,0,0,.1);
  font-weight: 700;
  border-radius: 5px;
  padding: .2rem .5rem;
  white-space: nowrap;
}
.suggestion-list {
  padding: .2rem;
  font-weight: 700;
  font-family: Roboto,sans-serif;
}
.suggestion-list__item.is-selected, .suggestion-list__item:hover {
  background-color: #1976d2;
  color: #fff;
}
.suggestion-list__item {
  border-radius: 5px;
  padding: .2rem .5rem;
  margin-bottom: .2rem;
  cursor: pointer;
}
.suggestion-list__item.is-empty {
  opacity: .5;
}
.suggestion-list__item:last-child {
  margin-bottom: 0;
}
</style>



// WEBPACK FOOTER //
// src/components/bot_v4/Detail/TemplateTextarea.vue